<template>
  <base-dialog
    :is-open="isOpen"
    max-width="600"
    @close="close"
  >
    <template #dialog-title>
      Zuwendungsbestätigung für Spendensäulen anfordern
    </template>
    <template #dialog-text>
      <v-form ref="form">
        <v-row>
          <v-col cols="12">
            <p class="mb-2">
              Jemand hat eine Zuwendungsbestätigung bei Ihnen angefordert? Geben Sie hier den Code und die Adressdaten für die Zuwendungsbestätigung ein.
            </p>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="codeDisplay"
              label="Spendensäulen-Code"
              placeholder="XXXX-XXXX"
              :rules="codeRules"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.name"
              label="Name SpenderIn"
              :rules="nameRules"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="form.address"
              label="vollständige Adresse SpenderIn"
              :rules="addressRules"
              outlined
              dense
              rows="3"
            />
          </v-col>
          <v-col cols="12">
            <v-alert
              type="warning"
              dense
            >
              Achtung, die Adressdaten können später nicht mehr geändert werden!
            </v-alert>
          </v-col>
          <v-col
            v-if="error"
            cols="12"
          >
            <v-alert
              type="error"
              dense
            >
              {{ error }}
            </v-alert>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template #dialog-actions>
      <v-btn @click="close">
        Abbrechen
      </v-btn>
      <v-btn
        color="primary"
        :disabled="saving"
        :loading="saving"
        @click="submit"
      >
        ZWB anfordern
      </v-btn>
    </template>
  </base-dialog>
</template>

<script>
import moment from 'moment'
import BaseDialog from '@/components/UI/BaseDialog.vue'
import { SET_SNACKBAR_MESSAGE, SET_SNACKBAR_WARNING_MESSAGE } from '@/modules/common/store/mutation-types'

const CODE_PATTERN = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/
const ALREADY_REDEEMED_PREFIX = 'Code bereits verwendet'
const CODE_NOT_FOUND_MESSAGE = 'Der Code konnte nicht überprüft werden, bitte überprüfen Sie auf Tippfehler und versuchen Sie es noch einmal.'

export default {
  name: 'RequestDonationReceiptDialog',
  components: { BaseDialog },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    organization: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'requested'],
  data () {
    return {
      form: {
        code: '',
        name: '',
        address: ''
      },
      saving: false,
      error: null
    }
  },
  computed: {
    codeDisplay: {
      get () {
        return this.form.code
      },
      set (value) {
        let clean = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
        if (clean.length > 4) {
          clean = `${clean.slice(0, 4)}-${clean.slice(4)}`
        }
        this.form.code = clean
      }
    },
    codeRules () {
      return [
        v => !!v || 'Bitte Code eingeben',
        v => CODE_PATTERN.test(v) || 'Code muss im Format XXXX-XXXX sein (8 Zeichen)'
      ]
    },
    nameRules () {
      return [
        v => !!v?.trim() || 'Bitte Name angeben'
      ]
    },
    addressRules () {
      return [
        v => !!v?.trim() || 'Bitte Adresse angeben'
      ]
    }
  },
  watch: {
    isOpen (val) {
      if (val) this.init()
    }
  },
  methods: {
    init () {
      this.error = null
      this.saving = false
      this.form = { code: '', name: '', address: '' }
      this.$refs.form?.resetValidation()
    },
    async submit () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.saving = true
      this.error = null
      try {
        const payload = {
          code: this.form.code.replace(/-/g, ''),
          person_data: {
            name: this.form.name,
            address: this.form.address,
            request_donation_receipt: true,
            allow_email_contact: true
          }
        }
        await this.$store.getters.restApi.post(
          `organization/${this.organization.id}/request_donation_receipt/`,
          payload
        )
        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: 'Code wurde erfolgreich überprüft. Das PDF für die Spendenbescheinigung wird erstellt, sobald auch die Zahlung bestätigt ist.',
          color: 'success'
        })
        this.$emit('requested')
        this.close()
      } catch (e) {
        const status = e.response?.status
        const detail = e.response?.data?.detail

        if (status === 404) {
          this.error = CODE_NOT_FOUND_MESSAGE
        } else if (status === 400 && typeof detail === 'string' && detail.startsWith(ALREADY_REDEEMED_PREFIX)) {
          const match = detail.match(/(\d{4}-\d{2}-\d{2})/)
          const formattedDate = match ? moment(match[1]).format('DD.MM.YYYY') : ''
          this.$store.commit(SET_SNACKBAR_WARNING_MESSAGE, {
            message: `Die Zuwendungsbestätigung für diesen Code wurde bereits an ${formattedDate} erstellt, bitte schauen Sie in der Liste nach.`
          })
          this.close()
        } else {
          this.error = CODE_NOT_FOUND_MESSAGE
        }
      } finally {
        this.saving = false
      }
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>
